/**
 * Admin DB Pro Library
 *
 * CRUD page for stock pro player videos in the `proVideos` collection.
 * Stock videos (isStock: true, coachId: null) are available to all coaches via
 * the Pro Library tab in the side-by-side comparison modal.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ref, uploadBytesResumable, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import {
  getStockProVideos,
  createProVideo,
  updateProVideo,
  deleteProVideo,
  preprocessProVideoForWeb,
} from '../../services/proVideos';
import type { ProVideo } from '../../types/lesson';
import { useToast } from '../../hooks/useToast';
import { generateThumbnailFromFile } from '../../utils/videoThumbnail';

// ── Helpers ───────────────────────────────────────────────────────────────────

function isPlaceholder(url: string) {
  return !url || url.startsWith('https://placeholder.pro-video/');
}

async function uploadStockVideo(
  videoId: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<{ url: string; path: string }> {
  const ext = file.name.split('.').pop() ?? 'mp4';
  // Versioned path — old URLs stay valid so lesson recordings remain unaffected
  const path = `pro-videos/stock/${videoId}/${Date.now()}.${ext}`;
  const storageRef = ref(storage, path);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type });
    task.on(
      'state_changed',
      (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, path });
      },
    );
  });
}

async function uploadStockThumbnail(videoId: string, blob: Blob): Promise<string> {
  const path = `pro-videos/stock/${videoId}/thumbnail.jpg`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
  return getDownloadURL(storageRef);
}

// ── Tag input ─────────────────────────────────────────────────────────────────

function TagInput({ tags, onChange }: { tags: string[]; onChange: (tags: string[]) => void }) {
  const [input, setInput] = useState('');

  function addTag(value: string) {
    const trimmed = value.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((tag) => (
          <span key={tag} className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full text-xs font-medium">
            {tag}
            <button type="button" onClick={() => onChange(tags.filter((t) => t !== tag))} className="text-indigo-400 hover:text-red-500 leading-none">×</button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) addTag(input); }}
        placeholder={tags.length === 0 ? 'Type a tag and press Enter' : 'Add another tag…'}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
      />
      <p className="text-xs text-gray-400 mt-1">Press Enter or comma to add. Examples: forehand, federer, topspin</p>
    </div>
  );
}

// ── Add / Edit Modal ──────────────────────────────────────────────────────────

interface ProVideoModalProps {
  video?: ProVideo;
  onClose: () => void;
  onSaved: (video: ProVideo, videoWasUploaded: boolean) => void;
}

function ProVideoModal({ video, onClose, onSaved }: ProVideoModalProps) {
  const { showError } = useToast();
  const [title, setTitle] = useState(video?.title ?? '');
  const [description, setDescription] = useState(video?.description ?? '');
  const [playerName, setPlayerName] = useState(video?.playerName ?? '');
  const [tags, setTags] = useState<string[]>(video?.tags ?? []);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [autoThumbBlob, setAutoThumbBlob] = useState<Blob | null>(null);
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState<string | null>(video?.thumbnailUrl ?? null);
  const thumbInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!video;
  const hasRealVideo = video && !isPlaceholder(video.videoUrl);

  useEffect(() => {
    if (!videoFile) return;
    let objectUrl: string | null = null;
    generateThumbnailFromFile(videoFile, 1).then((blob) => {
      setAutoThumbBlob(blob);
      if (!thumbnailFile) {
        objectUrl = URL.createObjectURL(blob);
        setThumbPreviewUrl(objectUrl);
      }
    }).catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFile]);

  function handleThumbnailFileChange(file: File | null) {
    setThumbnailFile(file);
    if (file) {
      setThumbPreviewUrl(URL.createObjectURL(file));
    } else {
      if (autoThumbBlob) {
        setThumbPreviewUrl(URL.createObjectURL(autoThumbBlob));
      } else {
        setThumbPreviewUrl(video?.thumbnailUrl ?? null);
      }
    }
  }

  const handleSave = useCallback(async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      if (isEditing) {
        let videoUrl = video.videoUrl;
        let videoStoragePath = video.videoStoragePath;
        if (videoFile) {
          const result = await uploadStockVideo(video.id, videoFile, setUploadProgress);
          videoUrl = result.url;
          videoStoragePath = result.path;
        }
        let thumbnailUrl = video.thumbnailUrl;
        const thumbSource = thumbnailFile ?? autoThumbBlob;
        if (thumbSource) {
          thumbnailUrl = await uploadStockThumbnail(video.id, thumbSource);
        }
        const updates: Parameters<typeof updateProVideo>[1] = {
          title: title.trim(),
          description: description.trim() || undefined,
          playerName: playerName.trim() || undefined,
          tags,
          videoUrl,
          videoStoragePath,
        };
        if (thumbnailUrl !== undefined) updates.thumbnailUrl = thumbnailUrl;
        await updateProVideo(video.id, updates);
        onSaved({ ...video, ...updates, thumbnailUrl }, !!videoFile);
      } else {
        const videoData: Omit<ProVideo, 'id' | 'createdAt'> = {
          title: title.trim(),
          description: description.trim() || undefined,
          playerName: playerName.trim() || undefined,
          tags,
          videoUrl: `https://placeholder.pro-video/${Date.now()}.mp4`,
          videoStoragePath: '',
          isStock: true,
          coachId: null,
        };
        const id = await createProVideo(videoData);
        if (videoFile) {
          const result = await uploadStockVideo(id, videoFile, setUploadProgress);
          await updateProVideo(id, { videoUrl: result.url, videoStoragePath: result.path });
          videoData.videoUrl = result.url;
          videoData.videoStoragePath = result.path;
        }
        const thumbSource = thumbnailFile ?? autoThumbBlob;
        if (thumbSource) {
          videoData.thumbnailUrl = await uploadStockThumbnail(id, thumbSource);
          await updateProVideo(id, { thumbnailUrl: videoData.thumbnailUrl });
        }
        onSaved({ id, ...videoData, createdAt: new Date().toISOString() } as ProVideo, !!videoFile);
      }
    } catch (err) {
      showError('Failed to save video');
      console.error(err);
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  }, [title, description, playerName, tags, videoFile, thumbnailFile, autoThumbBlob, video, isEditing, onSaved, showError]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
      <div data-testid="pro-video-modal" className="bg-white rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{isEditing ? 'Edit Pro Video' : 'Add Pro Video'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-400">*</span></label>
            <input
              data-testid="pro-video-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              placeholder="e.g. Federer Forehand Highlight"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Player Name</label>
            <input
              data-testid="pro-video-player-name-input"
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              placeholder="e.g. Roger Federer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
              placeholder="What makes this clip useful for comparison?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Tags</label>
            <TagInput tags={tags} onChange={setTags} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Video {isEditing ? (hasRealVideo ? '(replace existing)' : '(upload to replace placeholder)') : '(optional — can upload later)'}
            </label>
            {videoFile ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                <span className="text-sm text-gray-700 truncate">{videoFile.name}</span>
                <button onClick={() => { setVideoFile(null); setAutoThumbBlob(null); if (!thumbnailFile) setThumbPreviewUrl(video?.thumbnailUrl ?? null); }} className="ml-auto text-gray-400 hover:text-red-500 text-xs flex-shrink-0">Remove</button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg py-5 text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors text-sm"
              >
                Click to select video file
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Thumbnail <span className="text-gray-400 font-normal">(auto-generated from video if not set)</span>
            </label>
            <div className="flex items-start gap-3">
              <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
                {thumbPreviewUrl ? (
                  <img src={thumbPreviewUrl} alt="Thumbnail preview" className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-6 h-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24"><polygon points="6 4 20 12 6 20 6 4" /></svg>
                )}
              </div>
              <div className="flex-1 space-y-1.5">
                <button
                  type="button"
                  onClick={() => thumbInputRef.current?.click()}
                  className="w-full border border-gray-300 rounded-lg py-2 text-gray-600 hover:border-gray-400 hover:text-gray-800 transition-colors text-sm"
                >
                  {thumbPreviewUrl ? 'Replace thumbnail' : 'Upload thumbnail image'}
                </button>
                {thumbnailFile && (
                  <button type="button" onClick={() => handleThumbnailFileChange(null)} className="text-xs text-gray-400 hover:text-red-500">Remove custom thumbnail</button>
                )}
                <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleThumbnailFileChange(e.target.files?.[0] ?? null)} />
              </div>
            </div>
          </div>

          {uploadProgress !== null && (
            <div className="space-y-1">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="text-xs text-gray-500">Uploading… {uploadProgress}%</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            data-testid="save-pro-video-btn"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
            {saving ? (uploadProgress !== null ? 'Uploading…' : 'Saving…') : (isEditing ? 'Save changes' : 'Add video')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminProLibrary() {
  const [videos, setVideos] = useState<ProVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editVideo, setEditVideo] = useState<ProVideo | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProVideo | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [optimizingIds, setOptimizingIds] = useState<Set<string>>(new Set());
  const { showSuccess, showError } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const stock = await getStockProVideos();
      setVideos(stock);
    } catch {
      showError('Failed to load Pro Library');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  // Collect all unique tags across all videos for the filter bar
  const allTags = Array.from(new Set(videos.flatMap((v) => v.tags))).sort();

  const filtered = tagFilter === 'all'
    ? videos
    : videos.filter((v) => v.tags.includes(tagFilter));

  async function triggerOptimize(video: ProVideo) {
    setOptimizingIds((prev) => new Set(prev).add(video.id));
    try {
      const result = await preprocessProVideoForWeb(video.id, video.videoStoragePath, video.videoUrl);
      setVideos((prev) =>
        prev.map((v) =>
          v.id === video.id
            ? { ...v, webOptimizedVideoUrl: result.webOptimizedVideoUrl, webOptimizedStoragePath: result.webOptimizedStoragePath, durationSeconds: result.durationSeconds }
            : v
        )
      );
      showSuccess('Video optimized for web');
    } catch {
      showError('Failed to optimize video for web');
    } finally {
      setOptimizingIds((prev) => {
        const next = new Set(prev);
        next.delete(video.id);
        return next;
      });
    }
  }

  function handleSaved(saved: ProVideo, videoWasUploaded: boolean) {
    const videoForState = videoWasUploaded
      ? { ...saved, webOptimizedVideoUrl: undefined, webOptimizedStoragePath: undefined }
      : saved;
    setVideos((prev) => {
      const idx = prev.findIndex((v) => v.id === saved.id);
      const next = idx >= 0
        ? prev.map((v, i) => (i === idx ? videoForState : v))
        : [videoForState, ...prev];
      return next.sort((a, b) => a.title.localeCompare(b.title));
    });
    setShowAddModal(false);
    setEditVideo(null);
    showSuccess(editVideo ? 'Video updated' : 'Video added');

    if (videoWasUploaded && !isPlaceholder(saved.videoUrl)) {
      triggerOptimize(saved);
    }
  }

  async function handleDelete(video: ProVideo) {
    try {
      await deleteProVideo(video.id);
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
      showSuccess('Video deleted');
    } catch {
      showError('Failed to delete video');
    } finally {
      setConfirmDelete(null);
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">DB Pro Library</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading…' : `${videos.length} stock video${videos.length !== 1 ? 's' : ''} — available to all coaches in side-by-side comparisons`}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          data-testid="add-pro-video-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add Video
        </button>
      </div>

      {/* Tag filter */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-5">
          <button
            onClick={() => setTagFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${tagFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
          >
            All
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setTagFilter(tag)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                tagFilter === tag ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:border-gray-400'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-10 h-10 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
          </svg>
          <p className="text-sm">{videos.length === 0 ? 'No videos yet. Add the first one.' : 'No videos match the selected tag.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((video) => {
            const placeholder = isPlaceholder(video.videoUrl);
            const isPreviewing = previewId === video.id;
            const isOptimizing = optimizingIds.has(video.id);
            const isWebOptimized = !!video.webOptimizedVideoUrl;
            return (
              <div key={video.id} data-testid="pro-video-item" className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-start gap-3 p-4">
                  {/* Thumbnail */}
                  <div className="w-16 h-11 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center border border-gray-200">
                    {video.thumbnailUrl ? (
                      <img src={video.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 24 24"><polygon points="6 4 20 12 6 20 6 4" /></svg>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p data-testid="pro-video-title" className="font-semibold text-gray-900 text-sm">{video.title}</p>
                      {video.playerName && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{video.playerName}</span>
                      )}
                      {placeholder && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100">No video</span>
                      )}
                      {isOptimizing && (
                        <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                          <span className="w-2 h-2 border border-blue-400 border-t-blue-600 rounded-full animate-spin inline-block" />
                          Optimizing…
                        </span>
                      )}
                      {!isOptimizing && isWebOptimized && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-100">Web Optimized ✓</span>
                      )}
                    </div>
                    {video.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-0.5">
                        {video.tags.map((tag) => (
                          <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">{tag}</span>
                        ))}
                      </div>
                    )}
                    {video.description && (
                      <p className="text-xs text-gray-500 line-clamp-1">{video.description}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!placeholder && (
                      <button
                        onClick={() => setPreviewId(isPreviewing ? null : video.id)}
                        className={`px-2.5 py-1.5 text-xs border rounded-lg transition-colors ${isPreviewing ? 'border-blue-400 text-blue-600 bg-blue-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                      >
                        {isPreviewing ? 'Hide' : 'Preview'}
                      </button>
                    )}
                    {!placeholder && !isOptimizing && !isWebOptimized && (
                      <button
                        onClick={() => triggerOptimize(video)}
                        className="px-2.5 py-1.5 text-xs border border-indigo-200 rounded-lg hover:bg-indigo-50 text-indigo-600"
                      >
                        Optimize for Web
                      </button>
                    )}
                    <button
                      onClick={() => setEditVideo(video)}
                      data-testid="edit-pro-video-btn"
                      className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete(video)}
                      data-testid="delete-pro-video-btn"
                      className="px-2.5 py-1.5 text-xs border border-red-200 rounded-lg hover:bg-red-50 text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {isPreviewing && (
                  <div className="border-t border-gray-100 bg-black">
                    <video src={video.videoUrl} controls className="w-full max-h-60 object-contain" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {(showAddModal || editVideo) && (
        <ProVideoModal
          video={editVideo ?? undefined}
          onClose={() => { setShowAddModal(false); setEditVideo(null); }}
          onSaved={handleSaved}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Delete video?</h3>
            <p className="text-sm text-gray-600 mb-1">"{confirmDelete.title}" will be removed from the DB Pro Library.</p>
            <p className="text-xs text-gray-400 mb-5">Existing lesson recordings that used this video will not be affected.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} data-testid="confirm-delete-pro-video-btn" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
